import { AlertTypes, Utils } from "@/utils/Utils";
import { MaterialIcons } from "@/ui/icons";
import * as FileSystem from "@/platform/file-system";
import { manipulateAsync, SaveFormat } from "@/platform/image-manipulator";
import * as ImagePicker from "@/platform/image-picker";
import { forwardRef, memo, useImperativeHandle, useRef, useState } from "react";
import {
  Image,
  Keyboard,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "@/ui/rn";

/**
 * Peças COMUNS das telas de solicitação — Atestado Médico e Solicitação de Abono.
 *
 * As duas telas são a mesma tela com formulários diferentes: mesmo card, mesmo
 * detalhe, mesmo cancelamento, mesma folha de estilos. O que vive aqui é o que não
 * depende do tipo de solicitação: cores, helpers de data/texto, os componentes
 * pequenos (selo de sync, pill de status, foto com reserva, campo de observações
 * não-controlado) e o `styles` inteiro. Quem precisa de algo específico da própria
 * tela cria um `StyleSheet` próprio ao lado — não estende este.
 *
 * Extraído de `screens/AtestadoMedico.tsx` em 12/09/2026, quando o abono nasceu.
 */

const COLORS = {
  primary: "#122c36",
  // Azul do cabeçalho (o PRIMARY do BottomBar/PageFragment) — a cor de AÇÃO da tela:
  // FAB e botão de enviar. `primary` (#122c36) segue sendo a cor de texto/título.
  botao: "#294956",
  accent: "#0f6b85",
  green: "#1e8c5a",
  red: "#d94446",
  amber: "#b06423",
  background: "#f2f7f9",
  white: "#ffffff",
  border: "#eaf0f3",
  muted: "#8aa4ad",
  text: "#17323d",
  badgeGreenBg: "#e0f5eb",
  badgeGreenText: "#0f6e56",
  badgeAmberBg: "#fef3dc",
  badgeAmberText: "#854f0b",
  badgeRedBg: "#fdecec",
  badgeRedText: "#9b2c2c",
  sugestaoBg: "#e8f4f8",
  sugestaoText: "#0f6b85",
};

export type StatusSolicitacao = "PENDENTE" | "ACEITO" | "RECUSADO";

/**
 * Interruptores do POSTO para as duas telas de solicitação (16/09/2026), lidos do
 * `userData` — que é o `/iniciar` persistido em arquivo por tenant, então valem offline.
 *
 *   · atestado → `permite_enviar_atestado` (coluna nova em tab_adm_clientes; o t-credentials
 *     sonda a coluna e manda 'S' onde o ALTER ainda não rodou);
 *   · abono    → `permite_solicitar_abono` (a mesma coluna que o /iniciar sempre entregou
 *     como `permite_abono` — fica como reserva para servidor antigo).
 *
 * Só o 'N' EXPLÍCITO esconde/bloqueia; ausente ou vazio libera — a regra de todo
 * `permite_*` do app (ausente/vazio nunca regride quem já usa).
 */
const lerFlag = (userData: any, ...chaves: string[]): string => {
  for (const fonte of [userData, userData?.done, userData?.colaborador]) {
    if (!fonte) continue;
    for (const k of chaves) {
      const v = fonte?.[k];
      if (v != null && String(v).trim() !== "")
        return String(v).trim().toUpperCase();
    }
  }
  return "";
};
export const permiteEnviarAtestado = (userData: any): boolean =>
  lerFlag(userData, "permite_enviar_atestado") !== "N";
export const permiteSolicitarAbono = (userData: any): boolean =>
  lerFlag(userData, "permite_solicitar_abono", "permite_abono") !== "N";

/** Lado maior da foto guardada/enviada. Sobra para o RH ler e para o OCR (~1000 px já basta). */
const LARGURA_MAX_FOTO = 1600;

/**
 * Funil ÚNICO de foto das telas de solicitação (câmera ou galeria) — o mesmo desenho do
 * `pickFotoArquivo` do Livro de Ocorrência, que nasceu do crash "o Tirvu+ encerrou de forma
 * inesperada" ao anexar foto da galeria em aparelho fraco. Ver a nota
 * "Fotos do Livro (memória e ciclo de vida)" no vault. As regras, uma a uma:
 *
 *  · `quality: 1` no picker — qualquer valor menor faz o expo-image-picker decodificar a
 *    foto INTEIRA só para recomprimir (sem reduzir dimensão). Com 1 ele só copia o arquivo,
 *    e o único decode full-res fica no `manipulateAsync`, que é inevitável.
 *  · UMA redução, e NUNCA aumenta: só redimensiona se a origem for maior que o alvo (foto
 *    pequena da galeria saía maior e mais pesada do que entrou).
 *  · Falhou a redução → RECUSA a foto, com aviso. Ficar com a original seria voltar ao
 *    cenário que mata o processo (o OCR do atestado decodificaria os 12 MP inteiros).
 *  · Apaga o arquivo do picker do cache depois de reduzir — senão cresce até o disco encher.
 *  · `Keyboard.dismiss()` antes de abrir o seletor: campo focado dentro do sheet + seletor
 *    por cima é receita de teclado preso ao voltar.
 *
 * Devolve o `file://` da foto reduzida, ou `null` (cancelou, sem permissão, ou recusada —
 * os avisos já foram dados aqui).
 */
async function escolherFotoSolicitacao(
  fromCamera: boolean,
): Promise<string | null> {
  Keyboard.dismiss();
  const perm = fromCamera
    ? await ImagePicker.requestCameraPermissionsAsync()
    : await ImagePicker.requestMediaLibraryPermissionsAsync();
  if (!perm.granted) {
    Utils.Alert(
      AlertTypes.warning,
      fromCamera
        ? "Precisamos do acesso à câmera."
        : "Precisamos do acesso à galeria.",
      "Permissão necessária",
    );
    return null;
  }

  let result: ImagePicker.ImagePickerResult;
  try {
    result = fromCamera
      ? await ImagePicker.launchCameraAsync({
          mediaTypes: ["images"],
          quality: 1,
          allowsEditing: false,
        })
      : await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ["images"],
          quality: 1,
          allowsMultipleSelection: false,
          allowsEditing: false,
        });
  } catch (e) {
    console.warn("[Solicitação] seletor de foto falhou:", e);
    Utils.Alert(
      AlertTypes.danger,
      fromCamera ? "Erro ao abrir a câmera." : "Erro ao abrir a galeria.",
      "Erro",
    );
    return null;
  }
  if (result.canceled || !result.assets?.[0]?.uri) return null;
  const asset = result.assets[0];
  const uriOriginal = asset.uri;
  const larguraOriginal = Number(asset.width ?? 0);

  try {
    const precisaRedimensionar =
      larguraOriginal <= 0 || larguraOriginal > LARGURA_MAX_FOTO;
    const reduzida = await manipulateAsync(
      uriOriginal,
      precisaRedimensionar ? [{ resize: { width: LARGURA_MAX_FOTO } }] : [],
      { compress: 0.8, format: SaveFormat.JPEG },
    );
    if (reduzida?.uri) {
      if (reduzida.uri !== uriOriginal)
        FileSystem.deleteAsync(uriOriginal, { idempotent: true }).catch(
          () => {},
        );
      return reduzida.uri;
    }
  } catch (e) {
    // Log obrigatório: é o que separa "corrigido" de "caiu no caminho degradado".
    console.warn("[Solicitação] redução da foto falhou:", e);
  }

  Utils.Alert(
    AlertTypes.warning,
    "A imagem é grande demais para este aparelho. Feche outros apps e tente novamente.",
    "Não foi possível preparar a foto",
  );
  return null;
}

/**
 * "ATESTADO ILEGÍVEL, ENVIE OUTRA FOTO" → "Atestado Ilegível, Envie Outra Foto".
 * Baixa tudo antes de subir a 1ª letra de cada palavra: a resposta do fiscal costuma
 * vir em CAIXA ALTA do TirvuAdm, e `textTransform: "capitalize"` sozinho não baixa nada.
 */
const capitalizar = (s: string) =>
  String(s ?? "")
    .toLowerCase()
    .replace(
      /(^|[\s(\-"“'])(\p{L})/gu,
      (_m, antes, letra) => `${antes}${letra.toUpperCase()}`,
    );

/** `timestamp` (ms) → "DD/MM/YYYY HH:mm" local. */
const formatarEnvio = (ms: number) => {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const d = new Date(ms);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const pad2 = (n: number) => String(n).padStart(2, "0");
const isoDe = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

type ObservacoesHandle = { getValue: () => string; clear: () => void };

/**
 * Campo de observações ISOLADO e NÃO-CONTROLADO.
 *
 * ⚠️ Não troque por `value` + `setState`. Controlado, cada tecla re-renderiza a tela
 * inteira (o sheet, os 7 campos, o histórico), e no Android a corrida entre o `value`
 * controlado e o IME produz o "texto embolado" — caracteres fora de ordem, duplicados,
 * sumindo. É o mesmo bug já resolvido do mesmo jeito no campo de motivo da
 * `Justificativas` e no de mensagem do suporte (`BottomBar`): o texto vive num ref,
 * digitar não re-renderiza ninguém, e o pai lê `getValue()` na hora de enviar.
 *
 * O pai remonta por `key` a cada abertura do formulário — é o que zera o
 * `defaultValue` (setNativeProps não é confiável na New Arch).
 */
const ObservacoesInput = memo(
  forwardRef<ObservacoesHandle, { scrollRef: { current: any }; estilo: any }>(
    function ObservacoesInput({ scrollRef, estilo }, ref) {
      const inputRef = useRef<TextInput>(null);
      const valueRef = useRef("");
      useImperativeHandle(ref, () => ({
        getValue: () => valueRef.current,
        clear: () => {
          valueRef.current = "";
          inputRef.current?.clear();
        },
      }));
      return (
        <TextInput
          ref={inputRef}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
          defaultValue=""
          onChangeText={(t) => {
            valueRef.current = t;
          }}
          placeholder="Escreva o que achar importante para o seu fiscal"
          placeholderTextColor={COLORS.muted}
          style={estilo}
          maxLength={1000}
          autoCorrect={false}
          autoComplete="off"
          autoCapitalize="sentences"
          spellCheck={false}
          importantForAutofill="no"
          textContentType="none"
          keyboardType="default"
          // É o último campo: no foco, rola até o fim DEPOIS de o teclado subir (e
          // de o padding extra entrar). Sem o atraso, rola para um fim que ainda não
          // existe. 320 ms é o valor calibrado na Justificativas.
          onFocus={() =>
            setTimeout(
              () => scrollRef.current?.scrollToEnd?.({ animated: true }),
              320,
            )
          }
        />
      );
    },
  ),
);

/**
 * Selo de sincronização — a mesma regra do espelho de ponto: 1 check = salvo só neste
 * aparelho (vai subir sozinho), 2 checks = já está no servidor.
 *
 * NÃO é tocável (12/09/2026): era um Pressable com um alerta explicando o selo, e o
 * toque nele "engolia" o toque do card — a pessoa mirava o card e ganhava um aviso.
 * Como View simples, o toque passa para o card e abre o detalhe, como em qualquer
 * outro ponto dele. O significado do selo fica na acessibilidade.
 */
const SyncCheck = ({ synced }: { synced: boolean }) => (
  <View
    accessible
    accessibilityLabel={synced ? "Sincronizado" : "Somente neste aparelho"}
    style={styles.syncBadge}
  >
    <MaterialIcons
      name={synced ? "done-all" : "done"}
      size={14}
      color={synced ? "#1d9bf0" : COLORS.muted}
    />
  </View>
);

/** Asterisco vermelho de campo obrigatório — o único marcador da tela. */
const Obrig = () => <Text style={styles.asterisco}> *</Text>;

/** Cor e ícone do veredito — usados no anel da foto, no selo e no pill. */
const visualStatus = (st: StatusSolicitacao) =>
  st === "ACEITO"
    ? { cor: COLORS.green, icone: "check" as const }
    : st === "RECUSADO"
      ? { cor: COLORS.red, icone: "close" as const }
      : { cor: COLORS.amber, icone: "hourglass-empty" as const };

/**
 * `Image` com reserva: tenta `uri` e, se falhar (arquivo local apagado, ou sem rede para a
 * URL do servidor), troca para `reserva`. Use `key` com as duas fontes no chamador para o
 * estado zerar quando o item mudar. Sem fonte nenhuma, não renderiza (o chamador mostra o
 * ícone "sem foto").
 */
const FotoComReserva = memo(function FotoComReserva({
  uri,
  reserva,
  style,
  resizeMode,
}: {
  uri: string;
  reserva?: string;
  style: any;
  resizeMode?: "cover" | "contain";
}) {
  const [falhou, setFalhou] = useState(false);
  const fonte = !falhou ? uri : reserva || "";
  if (!fonte) return null;
  return (
    <Image
      source={{ uri: fonte }}
      style={style}
      resizeMode={resizeMode}
      onError={() => {
        if (!falhou && reserva) setFalhou(true);
      }}
    />
  );
});

function StatusPill({ status }: { status: StatusSolicitacao }) {
  const cfg =
    status === "ACEITO"
      ? {
          bg: COLORS.badgeGreenBg,
          fg: COLORS.badgeGreenText,
          icone: "check-circle" as const,
          texto: "Aceito",
        }
      : status === "RECUSADO"
        ? {
            bg: COLORS.badgeRedBg,
            fg: COLORS.badgeRedText,
            icone: "cancel" as const,
            texto: "Recusado",
          }
        : {
            bg: COLORS.badgeAmberBg,
            fg: COLORS.badgeAmberText,
            icone: "hourglass-empty" as const,
            texto: "Pendente",
          };
  return (
    <View style={[styles.pill, { backgroundColor: cfg.bg }]}>
      <MaterialIcons name={cfg.icone} size={14} color={cfg.fg} />
      <Text style={[styles.pillTexto, { color: cfg.fg }]}>{cfg.texto}</Text>
    </View>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <View style={styles.linha}>
      <Text style={styles.linhaRotulo}>{rotulo}</Text>
      <Text style={styles.linhaValor}>{valor}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  // ⚠️ SEM padding lateral próprio: o scroll do PageFragment já aplica 18 px, os
  // mesmos do `filtersRow`. Qualquer valor aqui SOMA a esses 18 e desalinha o conteúdo
  // das caixas do filtro — era isso que empurrava título e cards para dentro.
  conteudo: {
    paddingHorizontal: 0,
    paddingTop: 30,
    paddingBottom: 24,
    gap: 12,
  },

  semPermissao: {
    alignItems: "center",
    gap: 10,
    padding: 28,
    backgroundColor: COLORS.white,
    borderRadius: 18,
  },
  semPermissaoTexto: {
    color: COLORS.muted,
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
  },

  // FAB — mesmo desenho do da Ronda.
  fab: {
    position: "absolute",
    right: 14,
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: COLORS.botao,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 8,
    zIndex: 6,
  },

  // Filtros — copiados do Espelho de Ponto (filtersRow/periodBox/periodLabel/periodValue).
  // Mesmos valores do Espelho de Ponto. O padding lateral é obrigatório aqui: o slot
  // fixo do PageFragment não tem margem própria, então sem ele as caixas encostam nas
  // bordas da tela.
  filtersRow: {
    flexDirection: "row",
    paddingVertical: 5,
    gap: 8,
    alignItems: "stretch",
    paddingHorizontal: 18,
    marginTop: -26,
  },
  periodBox: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
    paddingVertical: 8,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOpacity: 0.04,
    shadowRadius: 10,
    elevation: 2,
  },
  periodLabel: {
    fontSize: 9,
    color: COLORS.muted,
    fontWeight: "600",
    textTransform: "uppercase",
    marginBottom: 2,
  },
  periodValue: { fontSize: 12, color: COLORS.primary, fontWeight: "800" },

  filtroOpcao: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingVertical: 13,
    paddingHorizontal: 12,
    borderRadius: 14,
    marginTop: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.white,
  },
  filtroOpcaoAtiva: {
    backgroundColor: COLORS.sugestaoBg,
    borderColor: "#b9dbe6",
  },
  filtroOpcaoTexto: {
    flex: 1,
    fontSize: 15,
    fontWeight: "600",
    color: COLORS.text,
  },
  filtroOpcaoTextoAtivo: { color: COLORS.accent, fontWeight: "800" },
  filtroContagem: {
    minWidth: 26,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: "#eef3f6",
    alignItems: "center",
  },
  filtroContagemAtiva: { backgroundColor: COLORS.white },
  filtroContagemTexto: { fontSize: 12, fontWeight: "800", color: COLORS.muted },
  filtroContagemTextoAtivo: { color: COLORS.accent },

  tituloSecao: {
    color: COLORS.muted,
    fontSize: 11.5,
    fontWeight: "800",
    letterSpacing: 1,
    textTransform: "uppercase",
    marginTop: 16,
    marginLeft: 0,
  },

  vazio: {
    alignItems: "center",
    gap: 8,
    padding: 28,
    backgroundColor: COLORS.white,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  vazioTitulo: { color: COLORS.text, fontSize: 15, fontWeight: "700" },
  vazioSub: {
    color: COLORS.muted,
    fontSize: 13,
    textAlign: "center",
    lineHeight: 19,
  },

  card: {
    backgroundColor: COLORS.white,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#e8eff2",
    padding: 14,
    shadowColor: "#0b2733",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.06,
    shadowRadius: 10,
    elevation: 2,
  },
  // Pressionado: afunda um fio e perde a sombra — responde ao dedo sem animação cara.
  cardPressionado: {
    transform: [{ scale: 0.99 }],
    shadowOpacity: 0.02,
    elevation: 0,
    backgroundColor: "#fbfdfe",
  },
  cardTopo: { flexDirection: "row", alignItems: "flex-start", gap: 8 },
  cardStatusLinha: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 6,
  },
  cardDataLinha: { flexDirection: "row", alignItems: "center", gap: 6 },
  cardData: {
    color: COLORS.text,
    fontSize: 14.4, // 16 − 10% (pedido de 12/09)
    fontWeight: "800",
    letterSpacing: -0.2,
    flexShrink: 1,
  },
  cardSeta: { color: COLORS.muted, fontWeight: "400" },
  chips: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 8 },
  // Canto de 7 px, não pílula: é o que separa os chips de DETALHE (tipo, dias, horas,
  // CID) do pill de STATUS, que continua redondo. Duas famílias, dois formatos.
  chip: {
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: "#dfe8ec",
    backgroundColor: COLORS.white,
  },
  chipForte: { backgroundColor: COLORS.botao, borderColor: COLORS.botao },
  chipTexto: { fontSize: 11.5, fontWeight: "700", color: "#526f7b" },
  chipTextoForte: { color: "#fff", fontWeight: "800" },
  cardMotivo: {
    color: COLORS.badgeRedText,
    fontSize: 12,
    fontStyle: "italic",
    marginTop: 8,
  },
  cardEnvioLinha: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    marginTop: 8,
  },
  cardEnvio: { color: COLORS.muted, fontSize: 11.5 },
  // Miniatura redonda da foto. Borda clara para a foto escura não "furar" o card.
  miniaturaWrap: { marginRight: 12, width: 56, height: 56 },
  // Anel na cor do status em volta da foto — 2,5 px, com um fio branco entre anel e
  // foto para a foto escura não "comer" o anel.
  miniaturaAnel: {
    width: 56,
    height: 56,
    borderRadius: 28,
    borderWidth: 2.5,
    padding: 2,
    backgroundColor: COLORS.white,
  },
  miniatura: {
    width: "100%",
    height: "100%",
    borderRadius: 999,
    backgroundColor: "#eef3f6",
  },
  miniaturaVazia: { alignItems: "center", justifyContent: "center" },
  // Selo do veredito no canto da foto, com borda branca para destacar da foto.
  miniaturaSelo: {
    position: "absolute",
    right: -2,
    bottom: -2,
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: COLORS.white,
    alignItems: "center",
    justifyContent: "center",
  },
  syncBadge: { padding: 2 },
  // Detalhe: a foto grande, com o convite de ampliar.
  detalheFotoRotulo: {
    color: COLORS.muted,
    fontSize: 11.5,
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginTop: 12,
    marginBottom: 6,
  },
  detalheFotoBox: {
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "#eef3f6",
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  detalheFoto: { width: "100%", height: 220 },
  detalheFotoLupa: {
    position: "absolute",
    right: 10,
    bottom: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "rgba(18,44,54,0.78)",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
  },
  detalheFotoLupaTexto: { color: "#fff", fontSize: 12, fontWeight: "700" },
  // Visualizador em tela cheia.
  visorFundo: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.94)",
    alignItems: "center",
    justifyContent: "center",
  },
  visorImagem: { width: "100%", height: "100%" },
  visorFechar: {
    position: "absolute",
    right: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(255,255,255,0.14)",
    alignItems: "center",
    justifyContent: "center",
  },
  tresPontos: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },

  pill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    alignSelf: "flex-start", // só o tamanho do conteúdo — não estica na linha
  },
  pillTexto: { fontSize: 12, fontWeight: "800" },
  offlinePill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: COLORS.badgeAmberBg,
  },
  offlineTexto: { fontSize: 11.5, fontWeight: "700", color: COLORS.amber },

  sheet: { paddingHorizontal: 20, paddingTop: 6, gap: 10 },
  sheetTitulo: {
    color: COLORS.primary,
    fontSize: 20,
    fontWeight: "800",
    letterSpacing: -0.3,
  },
  sheetSub: {
    color: COLORS.muted,
    fontSize: 13,
    lineHeight: 19,
    marginBottom: 4,
  },

  linha: {
    flexDirection: "row",
    gap: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  linhaRotulo: {
    width: 100,
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: "700",
  },
  linhaValor: { flex: 1, color: COLORS.text, fontSize: 14, fontWeight: "600" },
  linkFoto: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 12,
  },
  linkFotoTexto: {
    color: COLORS.accent,
    fontSize: 14,
    fontWeight: "700",
    textDecorationLine: "underline",
  },
  analise: {
    backgroundColor: COLORS.background,
    borderRadius: 14,
    padding: 14,
    gap: 4,
    marginTop: 6,
  },
  analiseTituloLinha: { flexDirection: "row", alignItems: "center", gap: 6 },
  analiseTitulo: { color: COLORS.primary, fontSize: 13, fontWeight: "800" },
  analiseMeta: { color: COLORS.muted, fontSize: 12 },
  // Tema "danger" do bloco quando a solicitação foi RECUSADA.
  analiseRecusada: {
    backgroundColor: COLORS.badgeRedBg,
    borderWidth: 1,
    borderColor: "#f1c9c9",
  },
  analiseTituloRecusada: { color: COLORS.badgeRedText },
  analiseMetaRecusada: { color: "#b05656" },
  analiseTextoRecusada: { color: COLORS.badgeRedText, fontWeight: "600" },
  analiseTexto: {
    color: COLORS.text,
    fontSize: 14,
    lineHeight: 20,
    marginTop: 2,
  },

  // Cancelamento (detalhe, só PENDENTE). Botão discreto — vermelho só no traço e no
  // texto; a caixa de confirmação é onde a cor "sobe", para o gesto ser deliberado.
  cancelarBotao: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 14,
    paddingVertical: 12,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: "#f1c9c9",
    backgroundColor: COLORS.white,
  },
  cancelarBotaoPressionado: { backgroundColor: COLORS.badgeRedBg },
  cancelarBotaoTexto: { color: COLORS.red, fontSize: 14, fontWeight: "800" },
  cancelarCaixa: {
    marginTop: 14,
    padding: 14,
    borderRadius: 14,
    backgroundColor: COLORS.badgeRedBg,
    gap: 6,
  },
  cancelarPergunta: {
    color: COLORS.badgeRedText,
    fontSize: 15,
    fontWeight: "800",
  },
  cancelarAviso: { color: COLORS.badgeRedText, fontSize: 13, lineHeight: 18 },
  cancelarAcoes: { flexDirection: "row", gap: 10, marginTop: 8 },
  cancelarManter: {
    flex: 1,
    paddingVertical: 11,
    borderRadius: 12,
    alignItems: "center",
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: "#f1c9c9",
  },
  cancelarManterTexto: { color: COLORS.text, fontSize: 14, fontWeight: "700" },
  cancelarConfirmar: {
    flex: 1,
    paddingVertical: 11,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: COLORS.red,
  },
  cancelarConfirmarTexto: { color: "#fff", fontSize: 14, fontWeight: "800" },

  rotulo: {
    color: COLORS.text,
    fontSize: 13.5,
    fontWeight: "700",
    marginTop: 8,
  },
  rotuloLinha: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 8,
  },
  asterisco: { color: COLORS.red, fontWeight: "900" },
  // Checkbox no padrão do "confirmo" do Livro de Ocorrência.
  semCidRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    padding: 12,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#e1e9ed",
    backgroundColor: COLORS.white,
    marginTop: 2,
  },
  semCidRowOn: { borderColor: "#b9dbe6", backgroundColor: COLORS.sugestaoBg },
  semCidTexto: { fontSize: 13.5, fontWeight: "700", color: COLORS.text },
  semCidSub: {
    fontSize: 11.5,
    color: COLORS.muted,
    marginTop: 2,
    lineHeight: 16,
  },
  opcional: { color: COLORS.muted, fontWeight: "500" },
  selo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: COLORS.sugestaoBg,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    marginTop: 8,
  },
  seloTexto: { color: COLORS.sugestaoText, fontSize: 11, fontWeight: "700" },

  fotoEscolha: { flexDirection: "row", gap: 10 },
  fotoBotao: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    backgroundColor: COLORS.accent,
    borderRadius: 16,
    paddingVertical: 18,
  },
  fotoBotaoSec: {
    backgroundColor: COLORS.sugestaoBg,
    borderWidth: 1,
    borderColor: "#cfe3ea",
  },
  fotoBotaoTexto: { color: "#fff", fontSize: 14, fontWeight: "800" },
  fotoBox: { borderRadius: 16, overflow: "hidden", backgroundColor: "#0b1519" },
  fotoPreview: { width: "100%", height: 260 },
  fotoLendo: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.45)",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  fotoLendoTexto: { color: "#fff", fontSize: 13, fontWeight: "700" },
  fotoAcoes: {
    flexDirection: "row",
    backgroundColor: COLORS.white,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
  },
  fotoAcao: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingVertical: 12,
  },
  fotoAcaoTexto: { color: COLORS.accent, fontSize: 13, fontWeight: "700" },

  tipoLinha: { flexDirection: "row", gap: 10 },
  tipoCard: {
    flex: 1,
    gap: 6,
    padding: 14,
    borderRadius: 16,
    backgroundColor: COLORS.white,
    borderWidth: 1.5,
    borderColor: COLORS.border,
  },
  tipoCardAtivo: { backgroundColor: COLORS.accent, borderColor: COLORS.accent },
  tipoTitulo: { color: COLORS.text, fontSize: 14, fontWeight: "800" },
  tipoTituloAtivo: { color: "#fff" },
  tipoSub: { color: COLORS.muted, fontSize: 11.5, lineHeight: 16 },
  tipoSubAtivo: { color: "#dcefF5" },

  campo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: "#d9e4ea",
    borderRadius: 14,
    paddingHorizontal: 12,
    minHeight: 52,
  },
  // Campo preenchido pelo OCR: fundo e borda no tom do selo. É o que diz QUAL campo
  // veio da foto quando o selo é compartilhado por mais de um (as horas).
  campoSugerido: { backgroundColor: COLORS.sugestaoBg, borderColor: "#b9dbe6" },
  // Legenda miúda de cada metade de um par (Início / Fim) — abaixo do rótulo do par.
  subRotulo: {
    color: COLORS.muted,
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginTop: 6,
    marginBottom: 4,
    marginLeft: 2,
  },
  campoSomenteLeitura: {
    backgroundColor: COLORS.background,
    borderStyle: "dashed",
  },
  campoIcone: {
    width: 30,
    height: 30,
    borderRadius: 9,
    backgroundColor: COLORS.sugestaoBg,
    alignItems: "center",
    justifyContent: "center",
  },
  campoInput: {
    flex: 1,
    color: COLORS.text,
    fontSize: 15,
    fontWeight: "600",
    paddingVertical: Platform.OS === "ios" ? 14 : 10,
  },
  campoRotuloInterno: { color: COLORS.muted, fontSize: 11 },
  campoValor: { color: COLORS.text, fontSize: 15, fontWeight: "700" },
  campoMulti: {
    alignItems: "flex-start",
    minHeight: 5 * 20 + 24,
    paddingVertical: 8,
  },
  campoInputMulti: { minHeight: 5 * 20, paddingVertical: 4 },
  horasLinha: { flexDirection: "row", gap: 10 },

  enviar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    backgroundColor: COLORS.botao,
    borderRadius: 16,
    paddingVertical: 16,
    marginTop: 14,
  },
  enviarTexto: { color: "#fff", fontSize: 15.5, fontWeight: "800" },
  rodapeAviso: {
    color: COLORS.muted,
    fontSize: 12,
    textAlign: "center",
    lineHeight: 17,
    marginTop: 2,
  },
});

export {
  COLORS,
  FotoComReserva,
  escolherFotoSolicitacao,
  Linha,
  Obrig,
  ObservacoesInput,
  StatusPill,
  SyncCheck,
  capitalizar,
  formatarEnvio,
  isoDe,
  pad2,
  styles,
  visualStatus,
};
export type { ObservacoesHandle };
